import { Link } from "wouter";
import { getAllBlogPosts } from "@/data/blogPosts";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";

export default function Blog() {
  const posts = getAllBlogPosts();

  return (
    <div className="min-h-screen">
      <SEO
        title="Industry Insights - Stanford Logistics Management"
        description="Data-driven analysis and research on M&A trends, technology transformation, and forces shaping the trucking and logistics industry."
        keywords="trucking industry trends, logistics M&A, driver shortage, freight technology, supply chain analysis, transportation insights"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary via-primary/95 to-primary/90 text-primary-foreground py-16 lg:py-24">
        <div className="container">
          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl mb-6">
              Industry Insights
            </h1>
            <p className="text-xl text-primary-foreground/90 leading-relaxed">
              Data-driven analysis and research on trends shaping the trucking and logistics industry
            </p>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {posts.map((post) => (
              <article
                key={post.id}
                className="bg-card rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow border border-border group"
              >
                {post.image && (
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="p-8">
                  <div className="flex items-center space-x-4 text-sm text-muted-foreground mb-4">
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-red-accent/10 text-red-accent font-medium">
                      {post.category}
                    </span>
                    <div className="flex items-center space-x-1">
                      <Calendar size={14} />
                      <time dateTime={post.date}>
                        {new Date(post.date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </time>
                    </div>
                  </div>

                  <h2 className="text-2xl font-bold mb-3 text-card-foreground group-hover:text-primary transition-colors">
                    <Link href={`/blog/${post.slug}`}>
                      <a>{post.title}</a>
                    </Link>
                  </h2>

                  <p className="text-muted-foreground leading-relaxed mb-6">
                    {post.excerpt}
                  </p>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-1 text-sm text-muted-foreground">
                      <Clock size={14} />
                      <span>{post.readTime}</span>
                    </div>
                    <Link href={`/blog/${post.slug}`}>
                      <Button variant="ghost" size="sm" className="group/btn">
                        Read More
                        <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
